"use client";
// app/shared/components/layout/DynamicColumns.tsx
// Renders header or footer from /api/layout/[section]/[param]
// Multi-row, mega menu, ticker, per-row colors.
// Full property support: html widget layout/animation/flash/rotation,
// advertise_block widget, site_image widget, expanded WidgetStyles.

import { Fragment, useEffect, useState, useRef, useCallback, useMemo, FormEvent } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

const ROTATE_MS = 3000;
// Fixed height reserved for the button row below a rotated image (padding
// + one line of button text). When a crossfade rotates between images
// where only SOME have button data, this constant lets the surrounding box
// reserve the same space on every frame — visible buttons on frames that
// have them, an invisible placeholder of the same height on frames that
// don't — so the box never grows/shrinks as the rotation cycles, which is
// what caused a layout jump every time it landed on/left a button image.
const BUTTON_ROW_RESERVE_PX = 35;
const BASE_MEDIA = (
  process.env.NEXT_PUBLIC_MEDIA_BASE_URL ?? "http://103.247.19.195:3001/uploads/upload_aapkiawaz/"
).replace(/\/$/, "");

// ── Types ──────────────────────────────────────────────────────
interface ImageButton    { title:string; link:string; }
interface ResolvedImage { id:number; path:string; link?:string; system_short_url?:string; alt?:string; buttons?:ImageButton[]; }
interface MenuLink      { label:string; url:string; }
interface NavChild      { id:number; label:string; url:string; istyle?:string; }
interface NavItem       { id:number; label:string; url:string; columns:NavChild[][]; istyle?:string; }
interface TickerItem    { id:number; title:string; system_short_url:string; }

// All CSS properties that the designer styleSection can produce
interface WidgetStyles {
  backgroundColor?:string;
  color?:string;
  fontFamily?:string;
  fontSize?:string;
  fontWeight?:string;
  fontStyle?:string;
  textDecoration?:string;
  textShadow?:string;
  padding?:string;
  margin?:string;
  border?:string;
  borderRadius?:string;
  outline?:string;
  boxShadow?:string;
  transform?:string;
  filter?:string;
  alignSelf?:string;
  flexGrow?:string|number;
  overflow?:string;
  scrollBehavior?:string;
}

// All data fields that any widget type can produce
interface WidgetData {
  // logo
  image_url?:string; site_name?:string; link?:string;
  // contact
  phone?:string; email?:string;
  // social
  items?:{platform:string;url:string}[];
  // copyright
  text?:string;
  // search
  placeholder?:string;
  // ticker
  label?:string; limit?:number; speed?:number;
  bg_color?:string; label_bg?:string; text_color?:string;
  // html widget — layout
  content?:string;
  width?:string; height?:string; max_width?:string;
  overflow?:string; display?:string; position?:string;
  z_index?:string; opacity?:string; visibility?:string;
  cursor?:string; pointer_events?:string; object_fit?:string;
  // html widget — typography
  text_align?:string; text_transform?:string;
  letter_spacing?:string; line_height?:string;
  word_break?:string; white_space?:string;
  // html widget — animation
  animation?:string; animation_duration?:string;
  animation_delay?:string; animation_iteration?:string;
  transition?:string;
  // html widget — timing
  rotation_speed?:string;
  flash_interval?:string; flash_duration?:string;
  // html widget — decoration
  box_shadow?:string;
  // html widget — custom
  custom_css_class?:string; custom_inline_css?:string;
  // advertise_block
  advertisement_id?:string; ad_name?:string;
  display_type?:string; rotation_delay?:string;
  // site_image
  image_name?:string; image_id?:string; img_name?:string;
  alt_text?:string; link_url?:string;
  [k:string]:unknown;
}

interface Widget        { type:string; data?:WidgetData; styles?:WidgetStyles; istyle?:string; device?:"mobile"|"desktop"; images?:ResolvedImage[]; menuColumns?:MenuLink[][]; megaMenuItems?:NavItem[]; resolvedNav?:{items:TickerItem[];menuType:string}; }
interface ColumnConfig  { colIndex:number; flexGrow:number; widgets:Widget[]; }
interface RowConfig     { rowIndex:number; columns:number; col_widths:string; row_margin:string; col_gap:number; row_height:string; bg_color:string; text_color:string; istyle?:string; columnConfigs:ColumnConfig[]; }
interface SectionConfig { hf_id:number; section:string; rows:RowConfig[]; }
interface ApiResponse   { success:boolean; config:SectionConfig|null; }

// ── Helpers ────────────────────────────────────────────────────
function imgUrl(p:string):string {
  if(!p) return "";
  // Already a full URL — encode only the path portion (after the origin) to
  // handle filenames with spaces, brackets, parentheses, etc.
  if(p.startsWith("http")||p.startsWith("//")) {
    try {
      const u=new URL(p);
      // Re-encode the pathname: decode first to avoid double-encoding,
      // then encode, but preserve forward slashes.
      u.pathname=u.pathname.split("/").map(seg=>encodeURIComponent(decodeURIComponent(seg))).join("/");
      return u.toString();
    } catch {
      // Fallback: manually encode spaces and common problem chars
      return p.replace(/ /g,"%20").replace(/\[/g,"%5B").replace(/\]/g,"%5D").replace(/\(/g,"%28").replace(/\)/g,"%29");
    }
  }
  // Relative path — strip leading slash, then encode each segment
  const clean=p.replace(/^\//,"");
  const encoded=clean.split("/").map(seg=>encodeURIComponent(decodeURIComponent(seg))).join("/");
  return `${BASE_MEDIA}/${encoded}`;
}

const TOKEN_RE = /^(site_logo|cat_logo|both_logo|image_short_url=|image_ids=|advertise_id=|advertise_short_url=|menu_ids=|mega_menu_ids=|user_mega_menu_ids=|img=)/i;
function stripTokens(raw:string):string {
  return raw.split(/\r?\n/).filter(line=>!TOKEN_RE.test(line.trim())).join("\n").trim();
}

// ── Shared logo/advertisement widget detection ──────────────────
// A widget counts as "logo content" or "advertisement content" for layout
// purposes (mobile merge/rotate, side-by-side-on-mobile, etc.) in two ways:
// 1. Its dedicated widget type — type:"logo", or type:"advertise"/"advertise_block".
// 2. One of the CMS's own shortcode tokens inside a generic html widget's
//    content — same tokens TOKEN_RE above already recognizes for stripping
//    from display. A CMS admin can build a "logo" out of a plain html widget
//    this way instead of the dedicated logo widget type, and it should still
//    be treated as one. Confirmed token set:
//      logo/image side:  site_logo · cat_logo · both_logo ·
//                         image_ids=[...] · image_short_url=[...] · img=
//      advertise side:   advertise_id=[...] · advertise_short_url=[...]
//    Only the token *name* is matched (not what follows the "="), so
//    id-list/slug-list syntax like image_ids=[70,28] matches the same as a
//    single id.
// Used by both RowRenderer (logo/image + ad as columns in the same row) and
// DynamicColumns (logo/image row + ad row as two adjacent rows) — one
// definition, shared, so a fix to detection doesn't need to be duplicated.
const LOGO_TOKEN_RE=/^\s*(site_logo|cat_logo|both_logo|image_ids=|image_short_url=|img=)/im;
const AD_TOKEN_RE=/^\s*(advertise_id=|advertise_short_url=)/im;
function isLogoLikeWidget(w:Widget):boolean {
  return w.type==="logo"||LOGO_TOKEN_RE.test(w.data?.content??"");
}
function isAdvertiseLikeWidget(w:Widget):boolean {
  return w.type==="advertise"||w.type==="advertise_block"||AD_TOKEN_RE.test(w.data?.content??"");
}

// ── Device visibility ("Show on" in the admin widget editor) ──
// widget.device: "mobile" = only screens up to 720px wide,
//                "desktop" = only screens 721px and wider,
//                missing   = every screen (all existing widgets).
// Hidden with plain CSS (HF_DEVICE_CSS, injected by DynamicColumns) — same
// "no JS/matchMedia" approach as the rest of this file, so no hydration
// mismatch or layout flash. NOTE: this 720px breakpoint is deliberately
// separate from the md (768px) breakpoint the layout itself uses.
type HfDevice="mobile"|"desktop";
const HF_DEVICE_CSS=`@media (max-width:720px){.hf-show-desktop{display:none!important}}@media (min-width:721px){.hf-show-mobile{display:none!important}}`;
function widgetDevice(w:Widget):HfDevice|null {
  return w.device==="mobile"||w.device==="desktop"?w.device:null;
}
function showsOn(w:Widget,d:HfDevice):boolean {
  const v=widgetDevice(w);
  return !v||v===d;
}
// The device shared by EVERY widget in the list, or null (empty, mixed, or
// any widget shown on all devices). Used to hide a whole row/column — gap,
// margin and background included — when nothing in it would show anyway.
function commonDevice(ws:Widget[]):HfDevice|null {
  if(!ws.length) return null;
  const d=widgetDevice(ws[0]);
  return d&&ws.every(w=>widgetDevice(w)===d)?d:null;
}
function deviceClass(d:HfDevice|null):string {
  return d?`hf-show-${d}`:"";
}

// Normalise a width/height value coming from designer data — add "px" if it's
// a bare number, pass through any other CSS length string as-is, drop
// "auto"/empty. Shared by every widget that renders a resolved image
// (AdvertiseBlockWidget, SiteImageWidget, the logo/ad rotation merge below)
// so there's one definition instead of copies that can drift apart.
function normDim(v:unknown):string|undefined {
  if(!v) return undefined;
  const s=String(v).trim();
  if(!s||s==="auto") return undefined;
  return /^\d+$/.test(s)?`${s}px`:s;
}

// Adds a fixed pixel amount onto a CMS-supplied height, for permanently
// reserving extra room (e.g. for a button row) alongside a declared image
// height. Only works when the value is a plain number or plain "Npx" —
// percentages/"auto"/etc can't be safely added to, so those are left
// untouched (returned as-is) rather than silently producing a wrong value.
function addPx(v:unknown,extraPx:number):string|undefined {
  if(!v) return undefined;
  const s=String(v).trim();
  const m=/^(\d+)(px)?$/.exec(s);
  if(!m) return s||undefined;
  return `${Number(m[1])+extraPx}px`;
}

// Strip a flow-breaking "position" out of a raw CMS style/data object before
// it's spread onto a widget's outer wrapper. The designer's styleSection can
// send arbitrary CSS properties (WidgetStyles isn't an exhaustive allowlist
// at runtime), and "absolute"/"fixed"/"sticky" on a generic content wrapper
// pulls the whole widget out of normal document flow — the space it used to
// take up collapses, later content shifts up into it, and the widget then
// renders floating on top of whatever ended up there. Only "relative"/
// "static" are safe here and are passed through unchanged.
function sanitizePositionIn<T extends Record<string,unknown>>(obj:T):T {
  const pos=obj.position;
  if(pos==="relative"||pos==="static"||pos===undefined) return obj;
  const {position:_omit,...rest}=obj;
  return rest as T;
}

// ── Rotation timing for a merged logo+advertise slot ─────────────
// When a row (or an adjacent row pair) mixes logo-like and advertise-like
// content and gets collapsed into a single rotating slot (see RowRenderer /
// RotatingRowPair), the slot's cadence comes from the LAST logo/advertise
// column in that group — not a fixed constant — so the CMS admin's own
// timing setting (rotation_delay on advertise_block, or rotation_speed on
// an html/advertise widget) is honored rather than overridden. Height is
// deliberately NOT sourced here: RotatingSlot always sizes itself to the
// tallest item's true rendered height, since a declared height on one
// column can be smaller than another item's actual content and clip it.
function getRotationSettings(widget:Widget|undefined):{delayMs:number} {
  if(!widget) return {delayMs:ROTATE_MS};
  const d=widget.data||{};
  const delaySec=Number(d.rotation_delay||d.rotation_speed||0);
  const delayMs=delaySec>0?delaySec*1000:ROTATE_MS;
  return {delayMs};
}

function istyleToClass(istyle:string|undefined):string {
  if(!istyle) return "";
  return istyle.split(",").map(s=>s.trim()).filter(Boolean).join(" ");
}

// Parse raw inline CSS string "color:red; font-size:14px" → React.CSSProperties
function parseInlineCss(raw:string):React.CSSProperties {
  if(!raw?.trim()) return {};
  const result:Record<string,string>={};
  raw.split(";").forEach(decl=>{
    const colon=decl.indexOf(":");
    if(colon<1) return;
    const prop=decl.slice(0,colon).trim();
    const val =decl.slice(colon+1).trim();
    if(!prop||!val) return;
    // Convert kebab-case → camelCase
    const camel=prop.replace(/-([a-z])/g,(_,c)=>c.toUpperCase());
    result[camel]=val;
  });
  return result as React.CSSProperties;
}

// CSS animation names supported by the designer
// Maps designer name → keyframe definition (injected once into <head>)
const ANIMATION_KEYFRAMES:Record<string,string>={
  fadeIn:       `@keyframes hfd-fadeIn{from{opacity:0}to{opacity:1}}`,
  fadeOut:      `@keyframes hfd-fadeOut{from{opacity:1}to{opacity:0}}`,
  slideInLeft:  `@keyframes hfd-slideInLeft{from{transform:translateX(-60px);opacity:0}to{transform:translateX(0);opacity:1}}`,
  slideInRight: `@keyframes hfd-slideInRight{from{transform:translateX(60px);opacity:0}to{transform:translateX(0);opacity:1}}`,
  slideInDown:  `@keyframes hfd-slideInDown{from{transform:translateY(-40px);opacity:0}to{transform:translateY(0);opacity:1}}`,
  slideInUp:    `@keyframes hfd-slideInUp{from{transform:translateY(40px);opacity:0}to{transform:translateY(0);opacity:1}}`,
  zoomIn:       `@keyframes hfd-zoomIn{from{transform:scale(0.7);opacity:0}to{transform:scale(1);opacity:1}}`,
  zoomOut:      `@keyframes hfd-zoomOut{from{transform:scale(1.3);opacity:0}to{transform:scale(1);opacity:1}}`,
  bounceIn:     `@keyframes hfd-bounceIn{0%{transform:scale(0.3);opacity:0}50%{transform:scale(1.1)}70%{transform:scale(0.9)}100%{transform:scale(1);opacity:1}}`,
  pulse:        `@keyframes hfd-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}`,
  shake:        `@keyframes hfd-shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}`,
  spin:         `@keyframes hfd-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`,
  flip:         `@keyframes hfd-flip{from{transform:perspective(400px) rotateY(0)}to{transform:perspective(400px) rotateY(360deg)}}`,
  wobble:       `@keyframes hfd-wobble{0%{transform:translateX(0)}15%{transform:translateX(-6px) rotate(-3deg)}30%{transform:translateX(5px) rotate(2deg)}45%{transform:translateX(-4px) rotate(-2deg)}60%{transform:translateX(3px) rotate(1deg)}75%{transform:translateX(-2px) rotate(-1deg)}100%{transform:translateX(0)}}`,
  rubberBand:   `@keyframes hfd-rubberBand{0%{transform:scaleX(1)}30%{transform:scaleX(1.25) scaleY(0.75)}40%{transform:scaleX(0.75) scaleY(1.25)}50%{transform:scaleX(1.15) scaleY(0.85)}65%{transform:scaleX(0.95) scaleY(1.05)}75%{transform:scaleX(1.05) scaleY(0.95)}100%{transform:scale(1)}}`,
  heartBeat:    `@keyframes hfd-heartBeat{0%{transform:scale(1)}14%{transform:scale(1.15)}28%{transform:scale(1)}42%{transform:scale(1.15)}70%{transform:scale(1)}}`,
  flash:        `@keyframes hfd-flash-kf{0%,50%,100%{opacity:1}25%,75%{opacity:0}}`,
};

// Inject animation keyframes into <head> once per animation name used
const injectedAnimations=new Set<string>();
function ensureAnimationKeyframe(name:string):void {
  if(name==="none"||!name||injectedAnimations.has(name)) return;
  const css=ANIMATION_KEYFRAMES[name];
  if(!css) return;
  const el=document.createElement("style");
  el.setAttribute("data-hfd-anim",name);
  el.textContent=css;
  document.head.appendChild(el);
  injectedAnimations.add(name);
}

// ── Social icons ───────────────────────────────────────────────
const SI:Record<string,React.ReactNode>={
  facebook: <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>,
  twitter:  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/></svg>,
  x:        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  instagram:<svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2}><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>,
  youtube:  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.97C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z"/><polygon fill="white" points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/></svg>,
  linkedin: <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>,
  whatsapp: <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.556 4.124 1.528 5.855L0 24l6.302-1.507A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.003-1.366l-.36-.214-3.733.893.924-3.642-.235-.374A9.818 9.818 0 1112 21.818z"/></svg>,
  telegram: <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M21.198 2.433a2.242 2.242 0 00-1.022.215l-16.5 6.668a2.25 2.25 0 00.126 4.21l3.265.916 1.605 5.477a1.028 1.028 0 001.737.37l2.267-2.322 4.088 3.024a2.25 2.25 0 003.497-1.39l2.75-16.5a2.25 2.25 0 00-2.813-2.668zm-1.318 3.567l-9.475 8.396-2.842-.797 12.317-7.599z"/></svg>,
};

// ── ImageButtons ───────────────────────────────────────────────
// Renders as its own row BELOW the image (normal document flow, not
// absolutely positioned over it) with spacing above it.
// Placement by count, based on the order buttons were added:
//   1 button  → right
//   2 buttons → 1st button on the RIGHT, 2nd button on the LEFT
//   3 buttons → 1st on the RIGHT, 2nd on the LEFT, 3rd in the MIDDLE
//
// reserveSpace: for a rotating set of images where only SOME carry button
// data — pass true (from the caller, based on whether ANY image in the
// whole set has buttons) so that even on a frame with no buttons, this
// renders an invisible placeholder at the same BUTTON_ROW_RESERVE_PX
// height instead of nothing. Otherwise the box's height would shrink on
// button-less frames and grow again on button frames — a layout jump on
// every rotation cycle rather than a one-time size difference.
const ImageButtons:React.FC<{buttons?:ImageButton[];reserveSpace?:boolean}>=({buttons,reserveSpace})=>{
  if(!buttons?.length){
    return reserveSpace
      ?<div style={{height:BUTTON_ROW_RESERVE_PX,visibility:"hidden"}} aria-hidden="true"/>
      :null;
  }
  const count=buttons.length;

  if(count===1){
    return(
      <div style={{display:"flex",justifyContent:"flex-end",gap:"0px",padding:"2px 0 0"}}>
        <BtnLink btn={buttons[0]}/>
      </div>
    );
  }

  if(count===3){
    return(
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"0px",padding:"2px 0 0"}}>
        {/* left = 2nd added */}
        <div style={{display:"flex",justifyContent:"flex-start"}}>
          <BtnLink btn={buttons[1]}/>
        </div>
        {/* middle = 3rd added */}
        <div style={{display:"flex",justifyContent:"center"}}>
          <BtnLink btn={buttons[2]}/>
        </div>
        {/* right = 1st added */}
        <div style={{display:"flex",justifyContent:"flex-end"}}>
          <BtnLink btn={buttons[0]}/>
        </div>
      </div>
    );
  }

  // count===2: left = 2nd added, right = 1st added
  return(
    <div style={{display:"flex",justifyContent:"space-between",gap:"0px",padding:"2px 0 0"}}>
      <BtnLink btn={buttons[1]}/>
      <BtnLink btn={buttons[0]}/>
    </div>
  );
};

const BtnLink:React.FC<{btn:ImageButton}>=({btn})=>(
  <a href={btn.link}
    target="_blank" rel="noopener noreferrer"
    style={{
      display:"inline-block",
      padding:"3px 3px",
      fontSize:"15px",
      fontWeight:600,
      color:"#fff",
      background:"rgba(0,0,0,0.85)",
      border:"1px solid rgba(0,0,0,0.15)",
      borderRadius:"4px",
      whiteSpace:"nowrap",
      textDecoration:"none",
    }}>
    {btn.title}
  </a>
);

// ── ImageRotator ───────────────────────────────────────────────
// Props accept optional width/height from the parent widget data so the
// rotator wrapper can act as a hard boundary and never overlap neighbours.
const ImageRotator:React.FC<{images:ResolvedImage[];width?:string;height?:string;objectFit?:React.CSSProperties["objectFit"]}>=({images,width,height,objectFit="contain"})=>{
  const [idx,setIdx]=useState(0);
  const ref=useRef<ReturnType<typeof setInterval>|null>(null);
  useEffect(()=>{
    if(images.length<=1) return;
    ref.current=setInterval(()=>setIdx(p=>(p+1)%images.length),ROTATE_MS);
    return()=>{if(ref.current) clearInterval(ref.current);};
  },[images.length]);
  if(!images.length) return null;
  const current=images[idx];

  // ── Single image: render in normal flow, no absolute positioning. ──
  // Its own natural size drives the box height, so it can never collapse
  // regardless of what height/min-height is or isn't configured — this is
  // the path a logo takes.
  if(images.length===1){
    const imgStyle:React.CSSProperties={
      display:"block",
      width: width||"100%",
      height: height||"auto",
      maxWidth:"100%",
      maxHeight: height?"100%":undefined,
      objectFit,
    };
    return(
      <div className="w-full" style={{maxWidth:"100%"}}>
        {current.link
          ?<a href={current.link} style={{display:"block"}}><img src={imgUrl(current.path)} alt={current.alt||""} style={imgStyle}/></a>
          :<img src={imgUrl(current.path)} alt={current.alt||""} style={imgStyle}/>}
        <ImageButtons buttons={current.buttons}/>
      </div>
    );
  }

  // ── Multiple images (crossfade rotator): these DO need position:absolute
  // to stack images in the same spot, so the wrapper needs a real height —
  // set width/height explicitly on the CMS widget (row height or widget
  // height) and the images fill it directly at 100%/100%.
  const wrapStyle:React.CSSProperties={
    position:"relative",
    overflow:"hidden",
    width: width||"100%",
    height: height||"auto",
    minHeight: height?undefined:60,
    maxWidth:"100%",
    display:"block",
  };

  const imgStyle:React.CSSProperties={
    display:"block",
    width:"100%",
    height:"100%",
    maxWidth:"100%",
    maxHeight:"100%",
    objectFit,
  };

  // Reserve the button row's space permanently (not just on frames where
  // the current image happens to have buttons) so rotating between images
  // with/without buttons never grows or shrinks the box — see
  // BUTTON_ROW_RESERVE_PX above.
  const hasAnyButtons=images.some(img=>img.buttons?.length);

  return(
    <div className="w-full" style={{maxWidth:"100%",height:hasAnyButtons?addPx(height,BUTTON_ROW_RESERVE_PX):undefined}}>
      <div style={wrapStyle} className="custom-height-section">
        {images.map((img,i)=>(
          <div key={img.id+"-"+i}
            style={{
              position:"absolute",inset:0,
              display:"flex",alignItems:"center",justifyContent:"center",
              opacity:i===idx?1:0,
              pointerEvents:i===idx?"auto":"none",
              transition:"opacity 0.7s",
            }}>
            {img.link
              ?<a href={img.link} style={{display:"block",width:"100%",height:"100%"}}>
                  <img src={imgUrl(img.path)} alt={img.alt||""} style={imgStyle}/>
               </a>
              :<img src={imgUrl(img.path)} alt={img.alt||""} style={imgStyle}/>}
          </div>
        ))}
      </div>
      {/* Buttons below the image, outside its fixed-height/overflow:hidden box */}
      <ImageButtons buttons={current.buttons} reserveSpace={hasAnyButtons}/>
    </div>
  );
};

// ── MegaNavItem ────────────────────────────────────────────────
const MegaNavItem:React.FC<{item:NavItem;linkStyle:React.CSSProperties}>=({item,linkStyle})=>{
  const anchorRef=useRef<HTMLDivElement>(null);
  const dropRef=useRef<HTMLDivElement>(null);
  const [open,setOpen]=useState(false);
  const [pos,setPos]=useState<{top:number;left:number}>({top:0,left:0});
  const hasDropdown=item.columns.length>0&&item.columns.some(col=>col.length>0);
  const timerRef=useRef<ReturnType<typeof setTimeout>|null>(null);

  const calcPos=()=>{
    if(!anchorRef.current) return;
    const rect=anchorRef.current.getBoundingClientRect();
    const estWidth=item.columns.length*165+48;
    const left=rect.left+estWidth>window.innerWidth-8?Math.max(8,rect.right-estWidth):rect.left;
    setPos({top:rect.bottom+4,left});
  };

  const handleMouseEnter=()=>{if(timerRef.current) clearTimeout(timerRef.current); calcPos(); setOpen(true);};
  const handleMouseLeave=()=>{timerRef.current=setTimeout(()=>setOpen(false),120);};
  const handleDropEnter=()=>{if(timerRef.current) clearTimeout(timerRef.current);};

  return (
    <div ref={anchorRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Link href={item.url}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded hover:bg-white/10 transition-colors whitespace-nowrap ${istyleToClass(item.istyle)}`}
        style={linkStyle}>
        {item.label}
        {hasDropdown&&<svg className="w-3 h-3 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><polyline points="6 9 12 15 18 9"/></svg>}
      </Link>
      {hasDropdown&&open&&typeof document!=="undefined"&&createPortal(
        <div ref={dropRef}
          className="fixed z-[9999] bg-white shadow-xl rounded-lg border border-gray-100 py-4 px-4 flex gap-8 fullwidth-class-menu"
          style={{top:pos.top,left:pos.left}}
          onMouseEnter={handleDropEnter} onMouseLeave={handleMouseLeave}>
          {item.columns.map((col,ci)=>{
            const [header,...links]=col;
            if(!header) return null;
            const colArr=col as NavChild[]&{__colIstyle?:string};
            return(
              <div key={ci} className={`flex flex-col gap-1 min-w-[140px] ${istyleToClass(colArr.__colIstyle)}`}>
                <Link href={header.url}
                  className={`block text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors whitespace-nowrap pb-1 border-b border-gray-100 mb-1 ${istyleToClass(header.istyle)}`}>
                  {header.label}
                </Link>
                {links.map(child=>(
                  <Link key={child.id} href={child.url}
                    className={`block text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 px-1 py-0.5 rounded transition-colors whitespace-nowrap ${istyleToClass(child.istyle)}`}>
                    {child.label}
                  </Link>
                ))}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
};

// ── MobileMenuDrawer ───────────────────────────────────────────
// Reusable slide-in drawer for a set of nav items. Can be driven by
// MegaMenuWidget's own trigger button, or externally (e.g. the header's
// consolidated mobile bar) via the `open`/`onClose` props.
const MobileMenuDrawer:React.FC<{items:NavItem[];open:boolean;onClose:()=>void;side?:"left"|"right";textColor?:string;initialOpenId?:number}>=({items,open,onClose,side="left",textColor,initialOpenId})=>{
  const [accordion,setAccordion]=useState<number|null>(null);
  const [q,setQ]=useState("");
  const [showSuggest,setShowSuggest]=useState(false);
  const pathname=usePathname();
  const router=useRouter();
  const ls:React.CSSProperties=textColor?{color:textColor}:{};
  useEffect(()=>{setAccordion(null);},[pathname]);
  useEffect(()=>{if(!open){setAccordion(null);setQ("");setShowSuggest(false);}},[open]);
  useEffect(()=>{if(open&&initialOpenId!=null) setAccordion(initialOpenId);},[open,initialOpenId]);
  const posClass=side==="left"?"left-0":"right-0";
  const closedTransform=side==="left"?"-translate-x-full":"translate-x-full";

  // ── In-drawer search/autocomplete ─────────────────────────────
  // Matched against this drawer's own item list (top-level items + every
  // nested mega-menu child) — same flatten helper the header search uses.
  // No result cap, same as the header/search-widget autocomplete.
  const entries=useMemo(()=>flattenNavItems(items,side),[items,side]);
  const suggestions=useMemo(()=>{
    const term=q.trim().normalize("NFC").toLowerCase();
    if(!term) return [];
    return entries.filter(e=>e.label.normalize("NFC").toLowerCase().includes(term));
  },[q,entries]);
  const selectSuggestion=(entry:SearchEntry)=>{
    setShowSuggest(false);
    setQ("");
    if(entry.kind==="heading"){
      // Heading: expand its submenu in place rather than navigating,
      // same behavior as clicking the heading row directly.
      setAccordion(entry.itemId);
    }else{
      onClose();
      router.push(entry.url);
    }
  };

  // Rendered via a portal straight into <body> — NOT as a normal descendant.
  // Reason: the sticky header wrapper (position:sticky + z-index) establishes
  // its own CSS stacking context, which traps any "position:fixed" descendant
  // (like this drawer) inside it. Without the portal, a CMS banner/ad elsewhere
  // on the page — especially one with its own z-index/position set via the
  // designer — can paint above the "open" drawer even though the drawer has
  // a high z-index, because that z-index is only compared within the trapped
  // stacking context, not against the whole page. Portaling to <body> guarantees
  // the drawer always paints in the root stacking context, on top of everything.
  const content=(
    <>
      {open&&<div className="fixed inset-0 bg-black/50 z-[9998]" onClick={onClose}/>}
      <div className={`fixed inset-y-0 ${posClass} w-72 bg-white z-[9999] shadow-2xl flex flex-col transform transition-transform duration-300 ${open?"translate-x-0":closedTransform}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
          <span className="font-semibold text-gray-800">Menu</span>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-800 rounded" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="relative px-3 py-2 border-b border-gray-100 shrink-0">
          <input
            type="text"
            value={q}
            onChange={e=>{setQ(e.target.value);setShowSuggest(true);}}
            onFocus={()=>setShowSuggest(true)}
            onBlur={()=>setTimeout(()=>setShowSuggest(false),150)}
            placeholder="Search menu..."
            className="w-full border border-gray-300 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
          />
          {showSuggest&&q.trim()&&suggestions.length>0&&(
            <ul className="absolute top-full left-3 right-3 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
              {suggestions.map((s,i)=>(
                <li key={s.url+i}>
                  <button type="button" onMouseDown={()=>selectSuggestion(s)} className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <span className="truncate">{s.label}</span>
                    {s.kind==="heading"&&<svg className="w-3.5 h-3.5 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><polyline points="9 18 15 12 9 6"/></svg>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {showSuggest&&q.trim()&&suggestions.length===0&&(
            <ul className="absolute top-full left-3 right-3 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <li className="px-3 py-2 text-sm text-gray-400">No matches</li>
            </ul>
          )}
        </div>
        <ul className="flex-1 overflow-y-auto py-2">
          {items.map((item,itemIndex)=>{
            // Flatten every column's entries into one plain list — no more
            // bold "sub heading" grouping level, just heading -> children.
            const children=item.columns.flat();
            const has=children.length>0;
            const isOpen=accordion===item.id;
            const rowBg=itemIndex%2===0?"bg-white":"bg-gray-50";
            return(
              <li key={item.id} className={rowBg}>
                <div className="flex items-center justify-between px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                  onClick={()=>{if(has) setAccordion(isOpen?null:item.id);}}>
                  {has?(
                    // Has children: this is a heading only — clicking opens/
                    // closes the submenu, it never navigates anywhere.
                    <span className={`text-sm text-gray-800 font-medium flex-1 ${istyleToClass(item.istyle)}`} style={ls}>
                      {item.label}
                    </span>
                  ):(
                    // No children: a plain link, since there's nothing to expand.
                    <Link href={item.url} className={`text-sm text-gray-800 font-medium flex-1 ${istyleToClass(item.istyle)}`} style={ls} onClick={onClose}>
                      {item.label}
                    </Link>
                  )}
                  {has&&<svg className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen?"rotate-180":""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><polyline points="6 9 12 15 18 9"/></svg>}
                </div>
                {has&&isOpen&&(
                  <div className="border-b border-gray-100">
                    {children.map((child,childIndex)=>(
                      <Link key={child.id} href={child.url} className={`flex items-center gap-2 pl-10 pr-4 py-1.5 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors ${childIndex%2===0?"bg-gray-50":"bg-gray-100"} ${istyleToClass(child.istyle)}`} onClick={onClose}>
                        <span className="w-1 h-1 rounded-full bg-gray-300 shrink-0"/>
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );

  if(typeof document==="undefined") return null;
  return createPortal(content,document.body);
};

// ── MegaMenuWidget ─────────────────────────────────────────────
const MegaMenuWidget:React.FC<{items:NavItem[];textColor?:string}>=({items,textColor})=>{
  const [drawerOpen,setDrawerOpen]=useState(false);
  const pathname=usePathname();
  useEffect(()=>{setDrawerOpen(false);},[pathname]);
  if(!items.length) return null;
  const ls:React.CSSProperties=textColor?{color:textColor}:{};
  return(
    <>
      <nav className="hidden md:flex flex-wrap items-center gap-x-1">
        {items.map(item=><MegaNavItem key={item.id} item={item} linkStyle={ls}/>)}
      </nav>
      <div className="md:hidden">
        <button onClick={()=>setDrawerOpen(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded border border-current/20 hover:bg-white/10 transition-colors" style={ls} aria-label="Open menu">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          Menu
        </button>
        <MobileMenuDrawer items={items} open={drawerOpen} onClose={()=>setDrawerOpen(false)} side="left" textColor={textColor}/>
      </div>
    </>
  );
};

// ── HeaderBar ────────────────────────────────────────────────
// Consolidated bar for the header at every screen size: left menu
// trigger, search box, right menu trigger — all in one row, regardless
// of how many separate nav rows the CMS admin has configured.
// The search input shows a live autocomplete dropdown matched against
// every menu label (top-level items + nested mega-menu children) as the
// person types — separate from the full-text /search results triggered
// on Enter/submit.
interface SearchEntry { label:string; url:string; kind:"heading"|"leaf"; itemId:number; side:string; }

const flattenNavItems=(items:NavItem[],side:string):SearchEntry[]=>{
  const seen=new Set<string>();
  const out:SearchEntry[]=[];
  const add=(label:string,url:string,kind:"heading"|"leaf",itemId:number)=>{
    const key=side+":"+(url||label);
    if(seen.has(key)) return;
    seen.add(key);
    out.push({label,url,kind,itemId,side});
  };
  for(const item of items){
    const children=item.columns.flat();
    if(children.length>0){
      // Headings aren't navigable in the drawer anymore — matching one in
      // search should open its submenu, not try to visit a URL.
      add(item.label,item.url,"heading",item.id);
      for(const child of children) add(child.label,child.url,"leaf",item.id);
    }else{
      add(item.label,item.url,"leaf",item.id);
    }
  }
  return out;
};

const HeaderBar:React.FC<{leftItems:NavItem[];rightItems:NavItem[]}>=({leftItems,rightItems})=>{
  const [openSide,setOpenSide]=useState<"left"|"right"|null>(null);
  const [leftInitialOpen,setLeftInitialOpen]=useState<number|undefined>(undefined);
  const [rightInitialOpen,setRightInitialOpen]=useState<number|undefined>(undefined);
  const [q,setQ]=useState("");
  const [showSuggest,setShowSuggest]=useState(false);
  const router=useRouter();
  const pathname=usePathname();
  useEffect(()=>{setOpenSide(null);setShowSuggest(false);},[pathname]);
  const allEntries=useMemo(()=>[...flattenNavItems(leftItems,"left"),...flattenNavItems(rightItems,"right")],[leftItems,rightItems]);
  const suggestions=useMemo(()=>{
    const term=q.trim().normalize("NFC").toLowerCase();
    if(!term) return [];
    // No slice/limit here — every matching menu item (top-level + nested
    // mega-menu children) is shown. The dropdown itself scrolls
    // (max-h + overflow-y-auto below) so an unbounded list stays usable.
    return allEntries.filter(e=>e.label.normalize("NFC").toLowerCase().includes(term));
  },[q,allEntries]);
  if(!leftItems.length&&!rightItems.length) return null;
  const onSubmit=(e:FormEvent)=>{e.preventDefault();setShowSuggest(false);if(q.trim()) router.push(`/search?q=${encodeURIComponent(q.trim())}`);};
  const selectSuggestion=(entry:SearchEntry)=>{
    setShowSuggest(false);
    if(entry.kind==="heading"){
      // Open the relevant drawer with this heading's submenu already expanded,
      // instead of navigating — headings are categories, not pages.
      setQ("");
      if(entry.side==="left") setLeftInitialOpen(entry.itemId); else setRightInitialOpen(entry.itemId);
      setOpenSide(entry.side);
    }else{
      setQ("");
      router.push(entry.url);
    }
  };
  const menuBtn=(side:"left"|"right")=>(
    <button onClick={()=>{if(side==="left") setLeftInitialOpen(undefined); else setRightInitialOpen(undefined);setOpenSide(side);}} aria-label="Open menu" className="p-2 rounded border border-gray-300 text-gray-700 shrink-0">
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
    </button>
  );
  return(
    <div className="flex items-center gap-2 w-full px-3 py-2 bg-white border-b border-gray-200">
      {leftItems.length>0?menuBtn("left"):<span className="w-9 shrink-0"/>}
      <form onSubmit={onSubmit} className="relative flex-1 flex items-center min-w-0 gap-1">
        <input
          type="text"
          value={q}
          onChange={e=>{setQ(e.target.value);setShowSuggest(true);}}
          onFocus={()=>setShowSuggest(true)}
          onBlur={()=>setTimeout(()=>setShowSuggest(false),150)}
          placeholder="Search the menu item ..."
          className="w-full min-w-0 border border-gray-300 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
        />
        <button type="submit" aria-label="Search" className="p-1.5 rounded-full bg-gray-100 hover:bg-blue-600 hover:text-white transition-colors shrink-0">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </button>
        {showSuggest&&suggestions.length>0&&(
          <ul className="absolute top-full left-0 right-9 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
            {suggestions.map((s,i)=>(
              <li key={s.side+s.url+i}>
                <button type="button" onMouseDown={()=>selectSuggestion(s)} className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  <span>{s.label}</span>
                  {s.kind==="heading"&&<svg className="w-3.5 h-3.5 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><polyline points="9 18 15 12 9 6"/></svg>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </form>
      {rightItems.length>0?menuBtn("right"):<span className="w-9 shrink-0"/>}
      <MobileMenuDrawer items={leftItems} open={openSide==="left"} onClose={()=>setOpenSide(null)} side="left" initialOpenId={leftInitialOpen}/>
      <MobileMenuDrawer items={rightItems} open={openSide==="right"} onClose={()=>setOpenSide(null)} side="right" initialOpenId={rightInitialOpen}/>
    </div>
  );
};
const TickerWidget:React.FC<{widget:Widget}>=({widget})=>{
  const d=widget.data||{};
  const items=(widget.resolvedNav?.items??[]) as TickerItem[];
  const speed=Number(d.speed||60),bg=(d.bg_color as string)||"#0000ff",lbg=(d.label_bg as string)||"#dc2626",tc=(d.text_color as string)||"#ffffff",label=(d.label as string)||"Breaking News";
  if(!items.length) return null;
  const doubled=[...items,...items];
  return(
    <div className="flex items-stretch overflow-hidden w-full" style={{background:bg,minHeight:40}}>
      <div className="shrink-0 px-4 flex items-center font-semibold text-sm whitespace-nowrap" style={{background:lbg,color:tc}}>{label}</div>
      <div className="flex-1 overflow-hidden relative flex items-center">
        <div className="flex whitespace-nowrap" style={{animation:`dc-scroll ${speed}s linear infinite`}}>
          {doubled.map((item,i)=>(
            <Link key={`${item.id}-${i}`} href={`/${item.system_short_url||"#"}`} className="inline-block mx-8 text-sm hover:underline" style={{color:tc}}>
              {item.title?.replace(/<[^>]*>/g,"")}
            </Link>
          ))}
        </div>
      </div>
      <style>{`@keyframes dc-scroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
    </div>
  );
};

// ── Small widget renderers ─────────────────────────────────────
const LogoWidget:React.FC<{data:WidgetData}>=({data})=>(
  <Link href={data.link||"/"} className="flex items-center gap-3 no-underline">
    {data.image_url&&<img src={data.image_url} alt={data.site_name||"logo"} className="h-16 w-auto object-contain"/>}
    {data.site_name&&<span className="text-xl font-bold text-gray-800">{data.site_name}</span>}
  </Link>
);
const ContactWidget:React.FC<{data:WidgetData}>=({data})=>(
  <div className="flex flex-col gap-1 text-sm">
    {data.phone&&<a href={`tel:${data.phone}`} className="hover:opacity-75">{data.phone}</a>}
    {data.email&&<a href={`mailto:${data.email}`} className="hover:opacity-75">{data.email}</a>}
  </div>
);
const SocialWidget:React.FC<{data:WidgetData}>=({data})=>{
  const visible=(data.items??[]).filter(i=>i.url?.trim());
  if(!visible.length) return null;
  return(<div className="flex items-center gap-3">{visible.map((item,i)=>(<a key={i} href={item.url} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 transition-colors" aria-label={item.platform}>{SI[item.platform.toLowerCase()]??<span className="text-xs uppercase">{item.platform}</span>}</a>))}</div>);
};
const CopyrightWidget:React.FC<{data:WidgetData;wStyle?:React.CSSProperties}>=({data,wStyle})=>
  data.text?<p className="text-sm text-gray-500" style={wStyle}>{data.text}</p>:null;

// The "search" widget type supports the same mega_menu_ids=/user_mega_menu_ids=
// shortcode tokens that html/advertise widgets do — the CMS admin attaches one
// of those tokens to this widget, the backend resolves it into megaMenuItems
// (same field MegaMenuWidget consumes), and this widget shows a live
// autocomplete dropdown against that menu's items as the person types.
// Falls back to a plain search box (no dropdown) if no menu is attached.
const SearchWidget:React.FC<{data?:WidgetData;megaMenuItems?:NavItem[]}>=({data,megaMenuItems=[]})=>{
  const [q,setQ]=useState("");
  const [show,setShow]=useState(false);
  const router=useRouter();

  const entries=useMemo(()=>flattenNavItems(megaMenuItems,"search-widget"),[megaMenuItems]);
  const suggestions=useMemo(()=>{
    const term=q.trim().normalize("NFC").toLowerCase();
    if(!term) return [];
    // No cap — every matching menu item is shown, same as the header's
    // own menu search (dropdown scrolls via max-h + overflow-y-auto below).
    return entries.filter(e=>e.label.normalize("NFC").toLowerCase().includes(term));
  },[q,entries]);

  const onSubmit=(e:FormEvent)=>{
    e.preventDefault();
    setShow(false);
    if(q.trim()) router.push(`/search?q=${encodeURIComponent(q.trim())}`);
  };
  const goTo=(url:string)=>{setShow(false);setQ("");router.push(url);};

  return(
    <form onSubmit={onSubmit} className="relative flex items-center gap-2 w-full sm:w-auto">
      <input
        type="text"
        value={q}
        onChange={e=>{setQ(e.target.value);setShow(true);}}
        onFocus={()=>setShow(true)}
        onBlur={()=>setTimeout(()=>setShow(false),150)}
        placeholder={(data?.placeholder as string)||"Search..."}
        className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500 w-full min-w-0 sm:w-40"
      />
      <button type="submit" className="p-1.5 rounded bg-gray-100 hover:bg-blue-600 hover:text-white transition-colors shrink-0" aria-label="Search">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </button>
      {show&&q.trim()&&suggestions.length>0&&(
        <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
          {suggestions.map((s,i)=>(
            <li key={s.url+i}>
              <button type="button" onMouseDown={()=>goTo(s.url)} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 truncate">
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
};

// ── FlatMenuWidget ─────────────────────────────────────────────
const FlatMenuWidget:React.FC<{cols:MenuLink[][];ls?:React.CSSProperties}>=({cols,ls})=>{
  if(!cols.length) return null;
  if(cols.length===1){
    return(<ul className="flex flex-wrap gap-x-4 gap-y-1 list-none m-0 p-0">{cols[0].map((l,i)=><li key={i}><Link href={l.url} className="text-sm hover:opacity-75 transition-opacity" style={ls}>{l.label}</Link></li>)}</ul>);
  }
  return(
    <div className="flex flex-wrap w-full gap-x-6 gap-y-4">
      {cols.map((col,ci)=>(
        <ul key={ci} className="flex flex-col gap-y-1 list-none m-0 p-0 min-w-[140px]" style={{flex:`1 1 ${Math.floor(100/cols.length)}%`}}>
          {col.map((l,i)=><li key={i}><Link href={l.url} className="text-sm hover:opacity-75 transition-opacity" style={ls}>{l.label}</Link></li>)}
        </ul>
      ))}
    </div>
  );
};

// ── AdvertiseBlockWidget ───────────────────────────────────────
const AdvertiseBlockWidget:React.FC<{widget:Widget;rowTextColor?:string}>=({widget,rowTextColor})=>{
  const d=widget.data||{};
  const images=widget.images||[];
  const styles=widget.styles||{};
  const rotDelay=Math.max(0,Number(d.rotation_delay||0))*1000||ROTATE_MS;

  const [adIdx,setAdIdx]=useState(0);
  const rotRef=useRef<ReturnType<typeof setInterval>|null>(null);
  useEffect(()=>{
    if(images.length<=1||!Number(d.rotation_delay||0)) return;
    rotRef.current=setInterval(()=>setAdIdx(p=>(p+1)%images.length),rotDelay);
    return()=>{if(rotRef.current) clearInterval(rotRef.current);};
  },[images.length,rotDelay,d.rotation_delay]);

  const w=normDim(d.width);
  const h=normDim(d.height);

  // Wrapper style: designer styles + dimensions + ALWAYS overflow:hidden + maxWidth:100%
  const wrapStyle:React.CSSProperties={
    ...sanitizePositionIn(Object.fromEntries(Object.entries(styles).filter(([,v])=>v))) as React.CSSProperties,
    ...(w?{width:w}:{}),
    ...(h?{height:h}:{}),
    overflow:"hidden",   // hard containment — never bleeds outside column
    maxWidth:"100%",     // never wider than its column
    display:"block",
  };

  const wrapperClass=`${istyleToClass(widget.istyle)} ${d.custom_css_class||""}`.trim();

  // img element style — fills wrapper, respects the CMS's own object-fit
  // setting (falls back to "contain"). This matters beyond pure visuals:
  // the button row below the image always aligns to the column's full
  // width, so if the image itself is letterboxed narrower than that (e.g.
  // "contain" shrinking a wide banner to fit the height), the button ends
  // up looking disconnected, off to the side of the visible artwork. When
  // the admin has explicitly set object_fit (e.g. "fill" for a banner meant
  // to be edge-to-edge), honoring it keeps the image and its buttons
  // visually aligned.
  const imgStyle:React.CSSProperties={
    display:"block",
    width:"100%",
    height:h?"100%":"auto",
    maxWidth:"100%",
    maxHeight:"100%",
    objectFit:(styles as React.CSSProperties)?.objectFit||(d.object_fit as React.CSSProperties["objectFit"])||"contain",
  };

  if(!images.length&&!d.content){
    return d.advertisement_id
      ?<div className={wrapperClass} style={wrapStyle} data-ad-id={d.advertisement_id} data-display-type={d.display_type}/>
      :null;
  }

  if(d.display_type==="html"&&d.content){
    return(
      <div className={wrapperClass} style={wrapStyle}
        dangerouslySetInnerHTML={{__html:d.content as string}}/>
    );
  }

  if(images.length){
    const current=images[adIdx];

    const makeImg=(img:ResolvedImage)=>{
      const el=<img src={imgUrl(img.path)} alt={img.alt||String(d.ad_name||"")} style={imgStyle}/>;
      return img.link
        ?<a href={img.link} target="_blank" rel="noopener noreferrer" style={{display:"block",width:"100%",height:"100%"}}>{el}</a>
        :el;
    };

    if(images.length===1){
      return(
        <div style={{width:"100%",maxWidth:"100%"}}>
          <div className={wrapperClass} style={{...wrapStyle,position:"relative"}}>
            {makeImg(current)}
          </div>
          <ImageButtons buttons={current.buttons}/>
        </div>
      );
    }

    // Multiple images — crossfade rotation
    const rotStyle:React.CSSProperties={
      position:"relative",
      width:"100%",
      height:h||"auto",
      minHeight:h?undefined:60,
      overflow:"hidden",
    };

    // Reserve the button row's space permanently — see BUTTON_ROW_RESERVE_PX.
    const hasAnyButtons=images.some(img=>img.buttons?.length);

    return(
      <div style={{width:"100%",maxWidth:"100%",height:hasAnyButtons?addPx(h,BUTTON_ROW_RESERVE_PX):undefined}}>
        <div className={wrapperClass} style={wrapStyle}>
          <div style={rotStyle}>
            {images.map((img,i)=>(
              <div key={img.id+"-"+i}
                style={{
                  position:"absolute",inset:0,
                  opacity:i===adIdx?1:0,
                  pointerEvents:i===adIdx?"auto":"none",
                  transition:"opacity 0.7s",
                }}>
                {makeImg(img)}
              </div>
            ))}
          </div>
        </div>
        {/* Buttons below the ad box, outside its overflow:hidden/fixed-height area */}
        <ImageButtons buttons={current.buttons} reserveSpace={hasAnyButtons}/>
      </div>
    );
  }

  return null;
};

// ── SiteImageWidget ────────────────────────────────────────────
const SiteImageWidget:React.FC<{widget:Widget}>=({widget})=>{
  const d=widget.data||{};
  const styles=widget.styles||{};
  const images=widget.images||[];

  const w=normDim(d.width);
  const h=normDim(d.height);

  // Wrapper: designer styles + dimensions + overflow:hidden + maxWidth:100%
  const wrapStyle:React.CSSProperties={
    ...sanitizePositionIn(Object.fromEntries(Object.entries(styles).filter(([,v])=>v))) as React.CSSProperties,
    ...(w?{width:w}:{}),
    ...(h?{height:h}:{}),
    overflow:"hidden",
    maxWidth:"100%",
    display:"block",
  };

  const wrapperClass=`${istyleToClass(widget.istyle)} ${d.custom_css_class||""}`.trim();

  const resolvedPath=images[0]?.path||(d.image_url as string)||"";
  const resolvedLink=images[0]?.link||(d.link_url as string)||"";
  const resolvedAlt =images[0]?.alt||(d.alt_text as string)||String(d.image_name||"");

  if(!resolvedPath) return null;

  const imgEl=(
    <img
      src={imgUrl(resolvedPath)}
      alt={resolvedAlt}
      style={{
        display:"block",
        width: w?"100%":"auto",
        height:h?"100%":"auto",
        maxWidth:"100%",
        maxHeight:"100%",
        objectFit:(styles as React.CSSProperties)?.objectFit||(d.object_fit as React.CSSProperties["objectFit"])||"contain",
      }}
    />
  );

  return(
    <div className={wrapperClass} style={wrapStyle}>
      {resolvedLink
        ?<a href={resolvedLink} style={{display:"block",width:"100%",height:"100%"}}>{imgEl}</a>
        :imgEl}
    </div>
  );
};

// ── HtmlWidget — full property support ────────────────────────
const HtmlWidget:React.FC<{widget:Widget;rowTextColor?:string}>=({widget,rowTextColor})=>{
  const {data={},images=[],menuColumns=[],megaMenuItems=[],styles}=widget;
  const d=data;
  const [flashOn,setFlashOn]=useState(false);
  const [rotIdx,setRotIdx]=useState(0);
  const flashRef=useRef<ReturnType<typeof setInterval>|null>(null);
  const rotRef =useRef<ReturnType<typeof setInterval>|null>(null);

  // ── Flash interval effect ──────────────────────────────────
  const flashIntervalSec=Number(d.flash_interval||0);
  const flashDurationMs=Number(d.flash_duration||400);
  useEffect(()=>{
    if(!flashIntervalSec) return;
    flashRef.current=setInterval(()=>{
      setFlashOn(true);
      setTimeout(()=>setFlashOn(false),flashDurationMs);
    },flashIntervalSec*1000);
    return()=>{if(flashRef.current) clearInterval(flashRef.current);};
  },[flashIntervalSec,flashDurationMs]);

  // ── Rotation interval effect (for multi-content blocks) ───
  const rotSpeedSec=Number(d.rotation_speed||0);
  // Count content blocks: images count + 1 if html content exists
  const contentCount=images.length+(stripTokens(d.content??"")? 1 : 0);
  useEffect(()=>{
    if(!rotSpeedSec||images.length<=1) return;
    rotRef.current=setInterval(()=>setRotIdx(p=>(p+1)%images.length),rotSpeedSec*1000);
    return()=>{if(rotRef.current) clearInterval(rotRef.current);};
  },[rotSpeedSec,images.length]);

  // ── Animation keyframe injection ──────────────────────────
  const animName=d.animation&&d.animation!=="none"?String(d.animation):"";
  useEffect(()=>{
    if(animName) ensureAnimationKeyframe(animName);
  },[animName]);

  // ── Build styles ──────────────────────────────────────────
  // 1. Start with styleSection (widget.styles) — all CSS props
  const stylesBase:React.CSSProperties=styles
    ?(sanitizePositionIn(Object.fromEntries(Object.entries(styles).filter(([,v])=>v&&v!==""))) as React.CSSProperties)
    :{};

  // 2. Merge data-level layout/typography/decoration fields
  const dataStyle:React.CSSProperties={};
  const s=(key:string,val:unknown,cssProp:keyof React.CSSProperties)=>{
    if(val&&String(val).trim()&&String(val)!=="none"&&String(val)!=="default"&&String(val)!=="auto"||
       (cssProp==="opacity"&&val!==undefined&&val!=="")
    ){
      (dataStyle as Record<string,unknown>)[cssProp]=val;
    }
  };

  // If any resolved image carries button data, the outer wrapper needs to
  // hold BOTH the image box (which already gets its own explicit d.height
  // internally, see the images-rendering block below) AND the button row
  // rendered below it. Forcing the plain image height onto this outer
  // wrapper would pin its layout height to the image alone — the button
  // row would then visually spill past the box's bottom edge into whatever
  // content renders next. So when buttons exist, the outer wrapper's
  // height is d.height PLUS a constant reserve for the button row
  // (BUTTON_ROW_RESERVE_PX) — a permanent, fixed amount of extra space,
  // not "auto" — so it never fluctuates as a crossfade rotates between
  // images that do/don't have buttons (see reserveSpace on ImageButtons
  // below, which keeps that row's own height constant across rotation too).
  const hasButtonImages=images.some(img=>img.buttons?.length);

  if(d.width)           dataStyle.width           = d.width;
  dataStyle.height = hasButtonImages ? addPx(d.height,BUTTON_ROW_RESERVE_PX) : (d.height||undefined);
  if(d.max_width)       dataStyle.maxWidth        = d.max_width;
  if(d.overflow&&d.overflow!=="")        dataStyle.overflow       = d.overflow as React.CSSProperties["overflow"];
  if(d.display&&d.display!=="block")     dataStyle.display        = d.display  as React.CSSProperties["display"];
  // "absolute"/"fixed"/"sticky" here would pull this widget's entire outer
  // wrapper out of normal document flow — the space it used to occupy
  // collapses, later content shifts up into it, and the widget then floats
  // on top of whatever that turned out to be. Only "relative"/"static" are
  // safe on a generic content wrapper like this, so anything else from the
  // CMS is ignored rather than applied.
  if(d.position==="relative"||d.position==="static") dataStyle.position = d.position as React.CSSProperties["position"];
  if(d.z_index)         dataStyle.zIndex          = d.z_index;
  if(d.opacity&&d.opacity!=="1")         dataStyle.opacity        = Number(d.opacity);
  if(d.visibility&&d.visibility!=="visible") dataStyle.visibility = d.visibility as React.CSSProperties["visibility"];
  if(d.cursor&&d.cursor!=="default")     dataStyle.cursor         = d.cursor as React.CSSProperties["cursor"];
  if(d.pointer_events&&d.pointer_events!=="auto") dataStyle.pointerEvents = d.pointer_events as React.CSSProperties["pointerEvents"];
  if(d.object_fit&&d.object_fit!=="")   dataStyle.objectFit      = d.object_fit as React.CSSProperties["objectFit"];
  if(d.text_align&&d.text_align!=="")   dataStyle.textAlign      = d.text_align as React.CSSProperties["textAlign"];
  if(d.text_transform&&d.text_transform!=="none") dataStyle.textTransform = d.text_transform as React.CSSProperties["textTransform"];
  if(d.letter_spacing)  dataStyle.letterSpacing   = d.letter_spacing;
  if(d.line_height)     dataStyle.lineHeight       = d.line_height;
  if(d.word_break&&d.word_break!=="")   dataStyle.wordBreak      = d.word_break as React.CSSProperties["wordBreak"];
  if(d.white_space&&d.white_space!=="") dataStyle.whiteSpace     = d.white_space as React.CSSProperties["whiteSpace"];
  if(d.box_shadow)      dataStyle.boxShadow        = d.box_shadow;
  if(d.transition)      dataStyle.transition       = d.transition;

  // 3. Animation
  const animStyle:React.CSSProperties={};
  if(animName){
    animStyle.animationName           = `hfd-${animName}`;
    animStyle.animationDuration       = d.animation_duration||"0.4s";
    animStyle.animationDelay          = d.animation_delay||"0s";
    animStyle.animationIterationCount = d.animation_iteration||"1";
    animStyle.animationFillMode       = "both";
    animStyle.animationTimingFunction = "ease";
  }

  // 4. Flash highlight overlay
  const flashStyle:React.CSSProperties=flashOn
    ?{outline:"3px solid rgba(255,180,0,0.85)",outlineOffset:"1px",transition:`outline ${flashDurationMs}ms ease`}
    :{transition:`outline ${flashDurationMs}ms ease`};

  // 5. Parse custom inline CSS (highest specificity — applied last)
  const inlineCssStyle=sanitizePositionIn(parseInlineCss(String(d.custom_inline_css||"")));

  // 6. Merge: stylesBase < dataStyle < animStyle < flashStyle < inlineCssStyle
  const baseStyle:React.CSSProperties={
    ...stylesBase,
    ...dataStyle,
    ...animStyle,
    ...flashStyle,
    ...inlineCssStyle,
  };

  // ── Link color fallback ───────────────────────────────────
  const ls:React.CSSProperties={};
  if(styles?.color) ls.color=styles.color;
  else if(rowTextColor) ls.color=rowTextColor;

  // ── Class names ───────────────────────────────────────────
  const wrapClass=[
    istyleToClass(widget.istyle),
    String(d.custom_css_class||""),
  ].filter(Boolean).join(" ");

  // ── Mega menu takes full control ──────────────────────────
  if(megaMenuItems.length>0) return <MegaMenuWidget items={megaMenuItems} textColor={ls.color}/>;

  // ── HTML content (strip resolved tokens) ─────────────────
  const html=stripTokens(d.content??"");
  const hasHtml=/<[a-z][\s\S]*>/i.test(html);

  return(
    <div className={wrapClass} style={Object.keys(baseStyle).length?baseStyle:undefined}>
      {/* Image rotator — uses rotation_speed from data */}
      {images.length>0&&(
        rotSpeedSec&&images.length>1
          ?<div style={{width:d.width||"100%",maxWidth:"100%",display:"block"}}>
            <div style={{width:"100%",height:d.height||undefined,maxWidth:"100%",overflow:"hidden",display:"block"}}>
              <div style={{position:"relative",width:"100%",height:d.height||"auto",minHeight:d.height?undefined:60,overflow:"hidden"}}>
                {images.map((img,i)=>{
                  const fit=(styles as React.CSSProperties)?.objectFit||(d.object_fit as React.CSSProperties["objectFit"])||"contain";
                  return(
                  <div key={img.id+"-"+i}
                    style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",opacity:i===rotIdx?1:0,pointerEvents:i===rotIdx?"auto":"none",transition:"opacity 0.7s"}}>
                    {img.link
                      ?<a href={img.link} style={{display:"block",width:"100%",height:"100%"}}>
                          <img src={imgUrl(img.path)} alt={img.alt||""} style={{display:"block",width:"100%",height:"100%",maxWidth:"100%",maxHeight:"100%",objectFit:fit}}/>
                       </a>
                      :<img src={imgUrl(img.path)} alt={img.alt||""} style={{display:"block",width:"100%",height:"100%",maxWidth:"100%",maxHeight:"100%",objectFit:fit}}/>}
                  </div>
                  );
                })}
              </div>
            </div>
            {/* Buttons below the crossfade box, outside its overflow:hidden/fixed-height area */}
            <ImageButtons buttons={images[rotIdx]?.buttons} reserveSpace={hasButtonImages}/>
          </div>
          :<ImageRotator images={images} width={String(d.width||"")} height={String(d.height||"")} objectFit={(styles as React.CSSProperties)?.objectFit||(d.object_fit as React.CSSProperties["objectFit"])||"contain"}/>
      )}
      {menuColumns.length>0&&<FlatMenuWidget cols={menuColumns} ls={ls}/>}
      {html&&(hasHtml
        ?<div className="text-sm" style={ls} dangerouslySetInnerHTML={{__html:html}}/>
        :<p className="text-sm whitespace-pre-line" style={ls}>{html}</p>
      )}
    </div>
  );
};

// ── WidgetRenderer ─────────────────────────────────────────────
// Wraps the widget only when it is limited to one device, so every widget
// shown on all devices renders exactly as before.
const WidgetRenderer:React.FC<{widget:Widget;rowTextColor?:string}>=({widget,rowTextColor})=>{
  const dev=widgetDevice(widget);
  const inner=<WidgetContent widget={widget} rowTextColor={rowTextColor}/>;
  return dev?<div className={deviceClass(dev)}>{inner}</div>:inner;
};

const WidgetContent:React.FC<{widget:Widget;rowTextColor?:string}>=({widget,rowTextColor})=>{
  const ic=istyleToClass(widget.istyle);
  switch(widget.type){
    case "logo":            return <div className={ic}><LogoWidget data={widget.data??{}}/></div>;
    case "contact":         return <div className={ic}><ContactWidget data={widget.data??{}}/></div>;
    case "social":          return <div className={ic}><SocialWidget data={widget.data??{}}/></div>;
    case "search":          return <div className={ic}><SearchWidget data={widget.data} megaMenuItems={widget.megaMenuItems}/></div>;
    case "copyright":       return <CopyrightWidget data={widget.data??{}} wStyle={widget.styles as React.CSSProperties}/>;
    case "ticker":          return <TickerWidget widget={widget}/>;
    case "html":
    case "advertise":       return <HtmlWidget widget={widget} rowTextColor={rowTextColor}/>;
    case "advertise_block": return <AdvertiseBlockWidget widget={widget} rowTextColor={rowTextColor}/>;
    case "site_image":      return <SiteImageWidget widget={widget}/>;
    default:                return null;
  }
};

// ── RowRenderer ────────────────────────────────────────────────
// Columns sit side-by-side on desktop (md and up), sized by each column's
// configured flexGrow as a strict percentage split (flex-basis:0%, the
// reliable behavior — NOT flex-basis:auto, which let one column swallow
// the row). On mobile, columns stack top-to-bottom, full width.
// Done with plain CSS breakpoints (no JS/matchMedia) so there's no
// layout flash and it works the same before/after hydration.
const RowRenderer:React.FC<{row:RowConfig;innerClassName?:string}>=({row,innerClassName=""})=>{
  const rowWidgets=row.columnConfigs.flatMap(c=>c.widgets);
  // Whole row is for one device only → hide it (with its gap/margin/bg) on the other.
  const rowDevClass=deviceClass(commonDevice(rowWidgets));

  // ── Sticky menu ──────────────────────────────────────────────
  // Any row that carries a nav/mega-menu widget (i.e. it wasn't already
  // consolidated into DynamicColumns' own headerBar — e.g. a row that mixes
  // logo + menu together) stays pinned to the top on scroll instead of
  // scrolling out of view with the rest of the page. Uses position:fixed
  // (not sticky) plus a measured spacer — sticky only stays stuck while its
  // own parent row is in view, so it would stop pinning as soon as you
  // scroll past this row's own height; fixed stays pinned for the whole page.
  const hasMenu=rowWidgets.some(w=>(w.megaMenuItems?.length??0)>0);
  const rowRef=useRef<HTMLDivElement>(null);
  const [rowHeight,setRowHeight]=useState(0);
  useEffect(()=>{
    if(!hasMenu) return;
    const el=rowRef.current;
    if(!el) return;
    const update=()=>setRowHeight(el.offsetHeight);
    update();
    const ro=new ResizeObserver(update);
    ro.observe(el);
    return()=>ro.disconnect();
  },[hasMenu]);

  // ── Logo + advertise shortcode columns: rotate through one slot ──
  // Only the columns that actually carry logo/advertise shortcode content
  // (site_logo / cat_logo / both_logo / image_ids= / image_short_url= /
  // img= / advertise_id= / advertise_short_url=, or the dedicated logo /
  // advertise(_block) widget types — see isLogoLikeWidget/isAdvertiseLikeWidget)
  // are pulled out and merged into ONE rotating slot on mobile only. Every
  // other column in the row (e.g. a menu column) is left exactly as-is, and
  // desktop keeps its normal side-by-side column layout untouched — same as
  // it worked before. Timing (rotation_delay on an advertise_block widget,
  // or rotation_speed on an html/advertise widget) and height are taken
  // from the LAST matched column, so whatever the CMS admin set there
  // governs the mobile rotation instead of a fixed constant.
  // The rotator is mobile-only, so desktop-only widgets don't count here —
  // otherwise it would rotate onto a slot whose content is hidden (blank).
  const isMobileLogoOrAd=(w:Widget)=>showsOn(w,"mobile")&&(isLogoLikeWidget(w)||isAdvertiseLikeWidget(w));
  const matchedColIndexes=new Set(
    row.columnConfigs.filter(col=>col.widgets.some(isMobileLogoOrAd)).map(c=>c.colIndex),
  );
  const matchedCols=row.columnConfigs.filter(col=>matchedColIndexes.has(col.colIndex));
  const mobileWidgets=rowWidgets.filter(w=>showsOn(w,"mobile"));
  const hasLogo=mobileWidgets.some(isLogoLikeWidget);
  const hasAdvertise=mobileWidgets.some(isAdvertiseLikeWidget);
  const rotateLogoAd=hasLogo&&hasAdvertise&&matchedCols.length>=2;

  const lastMatchedCol=matchedCols[matchedCols.length-1];
  const lastControllingWidget=lastMatchedCol?.widgets.find(isMobileLogoOrAd)??lastMatchedCol?.widgets[0];
  const {delayMs:rotDelayMs}=getRotationSettings(lastControllingWidget);

  const rs:React.CSSProperties={
    gap:`${row.col_gap??14}px`,
    margin:row.row_margin||"0",
    background:row.bg_color||undefined,
    color:row.text_color||undefined,
  };
  const stickyClass=hasMenu?"fixed top-0 left-0 right-0 z-50 shadow-sm":"";
  const stickyBg=hasMenu&&!row.bg_color?"bg-white":"";

  const renderColumnWidgets=(col:ColumnConfig)=>(
    <div className="flex flex-col gap-2 w-full">
      {col.widgets.map((widget,wi)=><WidgetRenderer key={wi} widget={widget} rowTextColor={row.text_color||undefined}/>)}
    </div>
  );

  // Desktop: plain side-by-side columns, exactly as before — never merged.
  const normalColumnNodes=row.columnConfigs.map(col=>(
    <div
      key={col.colIndex}
      className={`flex items-center min-w-0 w-full md:flex-[var(--col-flex)] ${deviceClass(commonDevice(col.widgets))}`}
      style={{"--col-flex":`${col.flexGrow} 0 0%`} as React.CSSProperties}
    >
      {renderColumnWidgets(col)}
    </div>
  ));

  // Mobile only: matched logo/advertise columns folded into one rotating
  // slot; any other column in the row (e.g. a menu column) still renders
  // normally alongside it, same as normalColumnNodes above.
  let mergedRotatorPlaced=false;
  const mobileColumnNodes=row.columnConfigs.map(col=>{
    if(matchedColIndexes.has(col.colIndex)){
      if(mergedRotatorPlaced) return null; // folded into the single slot below
      mergedRotatorPlaced=true;
      return(
        <div key="logo-ad-rotator" className="w-full">
          <RotatingSlot
            items={matchedCols.map(mc=><Fragment key={mc.colIndex}>{renderColumnWidgets(mc)}</Fragment>)}
            intervalMs={rotDelayMs}
          />
        </div>
      );
    }
    return(
      <div key={col.colIndex} className={`w-full ${deviceClass(commonDevice(col.widgets))}`}>
        {renderColumnWidgets(col)}
      </div>
    );
  });

  const desktopRowClass=`flex flex-col md:flex-row md:flex-wrap items-stretch w-full ${innerClassName} ${istyleToClass(row.istyle)} ${stickyClass} ${stickyBg} ${rowDevClass}`;

  const rowEl=rotateLogoAd?(
    <>
      {/* Mobile: logo/advertise columns rotate through one shared slot. */}
      <div className={`flex flex-col gap-2 w-full md:hidden ${innerClassName} ${istyleToClass(row.istyle)} ${stickyClass} ${stickyBg} ${rowDevClass}`} style={rs}>
        {mobileColumnNodes}
      </div>
      {/* Desktop: unchanged — normal side-by-side columns, as it was working. */}
      <div ref={rowRef} className={`hidden md:flex md:flex-wrap items-stretch w-full ${innerClassName} ${istyleToClass(row.istyle)} ${stickyClass} ${stickyBg} ${rowDevClass}`} style={rs}>
        {normalColumnNodes}
      </div>
    </>
  ):(
    <div ref={rowRef} className={desktopRowClass} style={rs}>
      {normalColumnNodes}
    </div>
  );

  if(!hasMenu) return rowEl;

  // Spacer reserves the space position:fixed removed the row from, so
  // whatever comes after it doesn't jump up underneath it.
  return(
    <>
      {rowEl}
      <div style={{height:rowHeight}} aria-hidden="true"/>
    </>
  );
};

// ── RotatingSlot ───────────────────────────────────────────────
// Generic crossfade rotator: shows one item at a time in a single slot,
// cycling every `intervalMs`. Shared by the logo+advertise column case
// above and RotatingRowPair below, so there's one rotation implementation
// instead of two copies that could drift apart.
//
// Sizing: every item is placed in the SAME CSS Grid cell (grid-area:"1/1"),
// so the browser sizes the grid row/track to the tallest item natively —
// on the very first paint, with no JavaScript measurement involved. This
// intentionally replaces an earlier version that used position:absolute +
// a ResizeObserver-measured height: that approach had a real gap between
// first paint (before the measurement effect ran) and the corrected
// height, during which a taller item — e.g. one with a button row under
// its image — could render past the container's still-too-small height
// and spill into whatever content came after it. Since nothing here is
// position:absolute, that failure mode can't happen: the grid track is
// always exactly as tall as the tallest item, immediately, so each item's
// own space (including any button row below its image) is always fully
// reserved and items can never disturb one another or the content below.
//
// Alignment: each item gets `alignSelf:"start"` — it sits at its own
// natural height starting from the top of the shared cell. A shorter item
// simply leaves empty space below it rather than being stretched to fill
// the tallest item's height.
const RotatingSlot:React.FC<{items:React.ReactNode[];intervalMs?:number}>=({items,intervalMs=ROTATE_MS})=>{
  const [idx,setIdx]=useState(0);
  useEffect(()=>{
    setIdx(0); // reset if the item set itself changes (e.g. columns re-resolve)
  },[items.length]);
  useEffect(()=>{
    if(items.length<=1) return;
    const id=setInterval(()=>setIdx(p=>(p+1)%items.length),intervalMs);
    return()=>clearInterval(id);
  },[items.length,intervalMs]);

  return(
    <div style={{display:"grid",width:"100%"}}>
      {items.map((item,i)=>(
        <div key={i}
          style={{gridArea:"1 / 1",alignSelf:"start",minWidth:0,overflow:"hidden",opacity:i===idx?1:0,pointerEvents:i===idx?"auto":"none",transition:"opacity 0.7s"}}>
          {item}
        </div>
      ))}
    </div>
  );
};

// ── RotatingRowPair ──────────────────────────────────────────────
// Some CMS layouts put the logo (+ QR/photo etc.) in one row and the
// advertisement in the very next row. Rendered normally that's two stacked
// blocks with their own margins — a visible gap, and separate on mobile.
// When exactly that adjacent logo-row → advertise-row (or reverse) pattern
// is detected, this renders both rows crossfading in the same slot instead —
// so only one is visible at a time and no extra stacked space.
const RotatingRowPair:React.FC<{rows:[RowConfig,RowConfig];innerClassName?:string}>=({rows,innerClassName})=>{
  // Same rule as the column-level merge in RowRenderer: cadence comes from
  // the LAST row's own controlling logo/advertise widget, not a fixed
  // constant — so whichever row the CMS admin configured last wins. Height
  // is always the tallest row's actual rendered height (see RotatingSlot).
  const lastRow=rows[rows.length-1];
  const lastWidget=lastRow.columnConfigs
    .flatMap(c=>c.widgets)
    .find(w=>isLogoLikeWidget(w)||isAdvertiseLikeWidget(w));
  const {delayMs}=getRotationSettings(lastWidget);
  return(
    <RotatingSlot
      items={rows.map(row=><RowRenderer key={row.rowIndex} row={row} innerClassName={innerClassName}/>)}
      intervalMs={delayMs}
    />
  );
};

// ── DynamicColumns ─────────────────────────────────────────────
interface DynamicColumnsProps { section:"header"|"footer"; className?:string; innerClassName?:string; }

// ── ContentSearchBar ─────────────────────────────────────────────
// A second, separate search box — distinct from the header's menu-item
// autocomplete (HeaderBar above). This one searches whatever content is
// ALREADY rendered on the current page (news, profiles, videos, etc.) —
// no API call. It works by scanning the DOM for every element tagged
// data-searchable-title="true" (added to NewsBlock's and VideoBlock's
// title links) and matching against their visible text.
// NOTE: any other content block that should be searchable this way
// (e.g. ProfileBlock) needs the same data-searchable-title="true"
// attribute added to its title/name link.
// Rendered only for section="header", right after the header's own rows.
interface PageMatch { title:string; href:string; }

const ContentSearchBar:React.FC=()=>{
  const [q,setQ]=useState("");
  const [suggestions,setSuggestions]=useState<PageMatch[]>([]);
  const [show,setShow]=useState(false);
  const router=useRouter();

  const runSearch=(term:string)=>{
    const trimmed=term.trim().normalize("NFC").toLowerCase();
    if(!trimmed){setSuggestions([]);return;}
    if(typeof document==="undefined") return;
    const nodes=Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-searchable-title]'));
    const seen=new Set<string>();
    const matches:PageMatch[]=[];
    for(const node of nodes){
      const title=(node.textContent||"").trim();
      const href=node.getAttribute("href")||"";
      if(!title||!href) continue;
      if(!title.normalize("NFC").toLowerCase().includes(trimmed)) continue;
      const key=href+"|"+title;
      if(seen.has(key)) continue;
      seen.add(key);
      matches.push({title,href});
      if(matches.length>=8) break;
    }
    setSuggestions(matches);
  };

  const onChange=(e:React.ChangeEvent<HTMLInputElement>)=>{
    setQ(e.target.value);
    setShow(true);
    runSearch(e.target.value);
  };

  const onSubmit=(e:FormEvent)=>{
    e.preventDefault();
    setShow(false);
    if(q.trim()) router.push(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const goTo=(href:string)=>{setShow(false);setQ("");router.push(href);};

  return(
    <div className="w-full bg-gray-50 border-b border-gray-200 px-4 py-3">
      <form onSubmit={onSubmit} className="relative max-w-xl mx-auto">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={q}
            onChange={onChange}
            onFocus={()=>{setShow(true);runSearch(q);}}
            onBlur={()=>setTimeout(()=>setShow(false),150)}
            placeholder="Search page content only..."
            className="w-full min-w-0 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-blue-500"
          />
          <button type="submit" aria-label="Search" className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shrink-0">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </button>
        </div>
        {show&&q.trim()&&suggestions.length>0&&(
          <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
            {suggestions.map((s,i)=>(
              <li key={s.href+i}>
                <button type="button" onMouseDown={()=>goTo(s.href)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 truncate">
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
        )}
        {show&&q.trim()&&suggestions.length===0&&(
          <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <li className="px-4 py-2 text-sm text-gray-400">No matches on this page</li>
          </ul>
        )}
      </form>
    </div>
  );
};

const DynamicColumns:React.FC<DynamicColumnsProps>=({section,className="",innerClassName=""})=>{
  const pathname=usePathname();
  const [config,setConfig]=useState<SectionConfig|null>(null);
  const [loading,setLoading]=useState(true);

  // ── Fixed header-bar height measurement ─────────────────────────
  // The bar itself uses position:fixed (see render below) so it is pinned to
  // the viewport for the entire page, not just while its parent is in view —
  // position:sticky only stays stuck within its own containing block, so it
  // would stop sticking once you scroll past the header section entirely.
  // Since position:fixed removes the bar from normal flow, a spacer of the
  // same height is rendered in its place so the rest of the header/page
  // doesn't jump up underneath it. ResizeObserver keeps this correct if the
  // bar's height changes (e.g. text wraps to 2 lines on a narrow screen).
  const headerBarRef=useRef<HTMLDivElement>(null);
  const [headerBarHeight,setHeaderBarHeight]=useState(0);

  useEffect(()=>{
    const el=headerBarRef.current;
    if(!el) return;
    const update=()=>setHeaderBarHeight(el.offsetHeight);
    update();
    const ro=new ResizeObserver(update);
    ro.observe(el);
    return()=>ro.disconnect();
  });

  useEffect(()=>{
    const segments=pathname.split("/").filter(Boolean);
    const slug    =segments[0]||"home";
    const referral=segments[1]??null;
    const apiUrl=referral
      ?`/api/layout/${section}/${slug}?referral=${encodeURIComponent(referral)}&home_page_url=${process.env.NEXT_PUBLIC_DEFAULT_SITE_URL_SHORT_URL}`
      :`/api/layout/${section}/${slug}?home_page_url=${process.env.NEXT_PUBLIC_DEFAULT_SITE_URL_SHORT_URL}`;

    let cancelled=false;
    setLoading(true);
    fetch(apiUrl)
      .then(r=>r.json() as Promise<ApiResponse>)
      .then(data=>{if(!cancelled&&data.success) setConfig(data.config);})
      .catch(err=>console.error("[DynamicColumns]",section,err))
      .finally(()=>{if(!cancelled) setLoading(false);});
    return()=>{cancelled=true;};
  },[pathname,section]);

  if(loading) return(
    <div className={`w-full ${className}`}>
      <div className={`flex items-center gap-4 ${innerClassName}`}>
        <div className="h-12 w-28 bg-gray-100 animate-pulse rounded"/>
        <div className="flex-1 h-10 bg-gray-100 animate-pulse rounded"/>
        <div className="h-10 w-36 bg-gray-100 animate-pulse rounded"/>
      </div>
    </div>
  );

  if(!config?.rows?.length) return null;

  // ── Header-only: consolidate every nav-only row into a single
  // left-menu / search / right-menu bar, at every screen size.
  // Left/right menus are picked separately for mobile and desktop, so a
  // menu widget set to "Mobile only"/"Desktop only" only appears in that
  // device's bar. With no device-limited menus both lists are identical and
  // a single bar renders exactly as before.
  let headerBar:React.ReactNode=null;
  let headerBarDevClass="";
  let navOnlyRowIndexes=new Set<number>();
  if(section==="header"){
    const menuSets:Record<HfDevice,NavItem[][]>={mobile:[],desktop:[]};
    for(const row of config.rows){
      const rowWidgets=row.columnConfigs.flatMap(c=>c.widgets);
      const rowMenus=rowWidgets.filter(w=>(w.megaMenuItems?.length??0)>0);
      const isNavOnlyRow=rowWidgets.length>0&&rowWidgets.length===rowMenus.length;
      if(isNavOnlyRow){
        navOnlyRowIndexes.add(row.rowIndex);
        for(const w of rowMenus){
          if(showsOn(w,"mobile"))  menuSets.mobile.push(w.megaMenuItems as NavItem[]);
          if(showsOn(w,"desktop")) menuSets.desktop.push(w.megaMenuItems as NavItem[]);
        }
      }
    }
    const {mobile:m,desktop:d}=menuSets;
    const sameOnBoth=m.length===d.length&&m.every((set,i)=>set===d[i]);
    if(sameOnBoth){
      if(m.length>0) headerBar=<HeaderBar leftItems={m[0]??[]} rightItems={m[1]??[]}/>;
    }else{
      headerBar=(
        <>
          {m.length>0&&<div className="hf-show-mobile"><HeaderBar leftItems={m[0]??[]} rightItems={m[1]??[]}/></div>}
          {d.length>0&&<div className="hf-show-desktop"><HeaderBar leftItems={d[0]??[]} rightItems={d[1]??[]}/></div>}
        </>
      );
      // Only one device has a bar → hide the fixed wrapper (and its shadow) on the other.
      if(!m.length) headerBarDevClass="hf-show-desktop";
      if(!d.length) headerBarDevClass="hf-show-mobile";
    }
  }

  // ── Merge adjacent logo-row / advertise-row pairs ────────────────
  // Detects the pattern: one row is logo content, the very next row is
  // advertisement content (or vice versa) — a common CMS layout where the
  // designer put them in separate rows rather than separate columns of the
  // same row. Rendered as-is that's two stacked blocks with a gap between
  // them, and each takes its own space on mobile. Merging them into one
  // RotatingRowPair crossfades between the two, so it's a single slot with
  // no extra stacked space, and never shows them "separately." Uses the
  // shared isLogoLikeWidget/isAdvertiseLikeWidget helpers defined near the
  // top of this file (type check + shortcode-token check) — same detection
  // RowRenderer uses for the same-row/column case, so both stay in sync.
  // Pairing only happens on mobile, so desktop-only widgets are ignored.
  const isLogoOnlyRow=(row:RowConfig):boolean=>{
    const widgets=row.columnConfigs.flatMap(c=>c.widgets).filter(w=>showsOn(w,"mobile"));
    return widgets.length>0&&widgets.some(isLogoLikeWidget)&&!widgets.some(isAdvertiseLikeWidget);
  };
  const isAdvertiseOnlyRow=(row:RowConfig):boolean=>{
    const widgets=row.columnConfigs.flatMap(c=>c.widgets).filter(w=>showsOn(w,"mobile"));
    return widgets.length>0&&widgets.some(isAdvertiseLikeWidget)&&!widgets.some(isLogoLikeWidget);
  };

  const visibleRows=config.rows.filter(row=>!navOnlyRowIndexes.has(row.rowIndex));
  const rowNodes:React.ReactNode[]=[];
  for(let i=0;i<visibleRows.length;i++){
    const row=visibleRows[i];
    const next=visibleRows[i+1];
    const pairsUp=next&&(
      (isLogoOnlyRow(row)&&isAdvertiseOnlyRow(next))||
      (isAdvertiseOnlyRow(row)&&isLogoOnlyRow(next))
    );
    if(pairsUp){
      // Mobile: crossfade the pair in one slot, timed/sized off the last
      // row's own settings (see RotatingRowPair) — fixes the stacked-gap /
      // "shown separately" issue. Desktop: unchanged — both rows render
      // normally, side by side or stacked exactly as originally designed.
      // Both layouts are in the DOM; CSS breakpoints toggle which one is
      // visible (same "no JS/matchMedia" approach RowRenderer already uses),
      // so there's no hydration mismatch or layout flash.
      rowNodes.push(
        <Fragment key={`${row.rowIndex}-${next.rowIndex}`}>
          <div className="md:hidden">
            <RotatingRowPair rows={[row,next]} innerClassName={innerClassName}/>
          </div>
          <div className="hidden md:block">
            <RowRenderer row={row} innerClassName={innerClassName}/>
            <RowRenderer row={next} innerClassName={innerClassName}/>
          </div>
        </Fragment>
      );
      i++; // consumed both rows
      continue;
    }
    rowNodes.push(<RowRenderer key={row.rowIndex} row={row} innerClassName={innerClassName}/>);
  }

  return(
    <div className={`w-full ${className}`}>
      <style>{HF_DEVICE_CSS}</style>
      {headerBar&&(
        <>
          <div ref={headerBarRef} className={`fixed top-0 left-0 right-0 z-50 bg-white shadow-sm ${headerBarDevClass}`}>
            {headerBar}
          </div>
          {/* Spacer — reserves the space position:fixed removed the bar from,
              so the rest of the header/page doesn't jump up underneath it. */}
          <div style={{height:headerBarHeight}} aria-hidden="true"/>
        </>
      )}
      {rowNodes}
      {section==="header"&&<ContentSearchBar/>}
    </div>
  );
};

export default DynamicColumns;